import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../features/meta/metaSlice";
import {
  selectIsAuthenticated,
  selectUser,
} from "../../../features/auth/authSlice";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Home: React.FC = () => {
  const darkMode = useSelector(selectDarkMode);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);

  // Sample farm data
  const [farms, setFarms] = useState([
    {
      id: 1,
      name: "Green Valley Farm",
      location: "California",
      size: "250 acres",
      owner: "John Smith",
    },
    {
      id: 2,
      name: "Sunrise Orchards",
      location: "Washington",
      size: "180 acres",
      owner: "Emily Johnson",
    },
    {
      id: 3,
      name: "Blue Ridge Ranch",
      location: "Colorado",
      size: "520 acres",
      owner: "Michael Brown",
    },
    {
      id: 4,
      name: "Golden Fields",
      location: "Iowa",
      size: "350 acres",
      owner: "Sarah Williams",
    },
    {
      id: 5,
      name: "Meadow View",
      location: "Wisconsin",
      size: "210 acres",
      owner: "David Miller",
    },
    {
      id: 6,
      name: "Evergreen Farms",
      location: "Oregon",
      size: "420 acres",
      owner: "Jennifer Taylor",
    },
    {
      id: 7,
      name: "Sunset Valley",
      location: "Texas",
      size: "680 acres",
      owner: "Robert Garcia",
    },
    {
      id: 8,
      name: "Mountain View Estate",
      location: "Montana",
      size: "890 acres",
      owner: "William Anderson",
    },
    {
      id: 9,
      name: "River Creek Farm",
      location: "Idaho",
      size: "310 acres",
      owner: "Patricia Moore",
    },
    {
      id: 10,
      name: "Pleasant Valley",
      location: "New York",
      size: "175 acres",
      owner: "James Wilson",
    },
    {
      id: 11,
      name: "Oak Ridge Farm",
      location: "Virginia",
      size: "290 acres",
      owner: "Elizabeth Davis",
    },
    {
      id: 12,
      name: "Pine Grove",
      location: "Michigan",
      size: "430 acres",
      owner: "Thomas Martin",
    },
    {
      id: 13,
      name: "Maple Creek",
      location: "Vermont",
      size: "160 acres",
      owner: "Nancy Thompson",
    },
    {
      id: 14,
      name: "Harvest Hills",
      location: "Kansas",
      size: "540 acres",
      owner: "Richard White",
    },
    {
      id: 15,
      name: "Cedar Valley",
      location: "Minnesota",
      size: "395 acres",
      owner: "Margaret Clark",
    },
  ]);

  // Filter states
  const [nameFilter, setNameFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const farmsPerPage = 10;

  // Filter function
  const filteredFarms = farms.filter(
    (farm) =>
      farm.name.toLowerCase().includes(nameFilter.toLowerCase()) &&
      farm.location.toLowerCase().includes(locationFilter.toLowerCase())
  );

  // Calculate pagination values
  const totalPages = Math.ceil(filteredFarms.length / farmsPerPage);
  const indexOfLastFarm = currentPage * farmsPerPage;
  const indexOfFirstFarm = indexOfLastFarm - farmsPerPage;
  const currentFarms = filteredFarms.slice(indexOfFirstFarm, indexOfLastFarm);

  // Handle page changes
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <Card className="w-full rounded-xl">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Farm Management</CardTitle>
                <CardDescription>Farms</CardDescription>
              </div>
              <Button className="ml-auto bg-green-900 hover:bg-green-800">
                Add New Farm
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1 block">
                    Farm Name
                  </label>
                  <Input
                    placeholder="Filter by name..."
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1 block">
                    Location
                  </label>
                  <Input
                    placeholder="Filter by location..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNameFilter("");
                      setLocationFilter("");
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Farm Name</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Owner</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentFarms.length > 0 ? (
                      currentFarms.map((farm) => (
                        <TableRow key={farm.id}>
                          <TableCell className="font-medium">
                            {farm.name}
                          </TableCell>
                          <TableCell>{farm.location}</TableCell>
                          <TableCell>{farm.size}</TableCell>
                          <TableCell>{farm.owner}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-4">
                          No farms found matching the filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      currentPage > 1 && handlePageChange(currentPage - 1)
                    }
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => handlePageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      currentPage < totalPages &&
                      handlePageChange(currentPage + 1)
                    }
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Home;
